import { SiteGroupDrawer } from "@components";
import {
  Box,
  Stack,
  useMediaQuery,
  Badge,
  Tab,
  Tabs,
  Skeleton,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PortaledRightDrawer from "@pages/Layout/PortaledRightDrawer";
import { theme } from "@theme";
import OrderDetailsRow, {
  getDisplayOption,
} from "@pages/Order/components/OrderDetailsRow/OrderDetailsRow";
import {
  OrderOut,
  SiteGroupOut,
  useWebApiV1CloneOrderMutation,
  WebApiV1CloneOrderApiResponse,
} from "@providers/hop-ord-server/api";
import { useCommentsApiV1GetUnreadCommentCountQuery } from "@providers/hop-ord-server/api";
import { Comments } from "@components";
import { useState } from "react";
import { useCheckUserRole, toLocaleDateTimeString } from "@utils";
import SiteGroupPanelSkeleton from "@components/SiteGroupPanel/SiteGroupPanelSkeleton";
import { useNavigate } from "react-router-dom";
import { Role } from "@enums";

export type PanelType = "comments" | "details";
export const DEFAULT_DRAWER = "details";

const PLAN_DELIVERY_OPTIONS = [
  { id: "express", label: "Express" },
  { id: "standard", label: "Standard" },
];

interface Props {
  selectedPanel: PanelType;
  orderId: number;
  setSelectedPanel: (value: PanelType) => void;
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  orderData?: OrderOut;
  siteGroupData: SiteGroupOut[];
  forceCollapsed?: boolean;
}

const OrderTrackerDrawer = ({
  setOpen,
  orderId,
  open: drawerOpen,
  setSelectedPanel,
  selectedPanel,
  orderData,
  siteGroupData,
  forceCollapsed = false,
}: Props) => {
  const screenIsLarge = useMediaQuery(theme.breakpoints.up("xl"));
  const [siteGroupPanelLoading, setSiteGroupPanelLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const isDosimetrist = useCheckUserRole(Role.DOSIMETRIST);

  const disableEditing =
    orderData?.createdFromLumonusAi ||
    orderData?.latestPlanGenerationAttempt !== null;

  const [cloneOrder] = useWebApiV1CloneOrderMutation();
  const navigate = useNavigate();

  const handleEditOrder = () => {
    if (!orderId) {
      return;
    }
    // If order is in draft status continue editing, otherwise clone the order
    if (!orderData?.isLatest) {
      if (orderData?.latestChildId) {
        navigate(`/order/${orderData?.latestChildId}`);
      } else {
        return;
      }
    } else {
      cloneOrder({ orderId })
        .then((res) => {
          const payload = (
            res as unknown as { data: WebApiV1CloneOrderApiResponse }
          ).data;
          navigate(`/order/${payload}`);
        })
        .finally(() => {
          setIsEditing(false);
        });
    }
  };

  const { data: unreadCommentCount } =
    useCommentsApiV1GetUnreadCommentCountQuery(
      {
        orderGroupId: orderData?.orderGroupId as number,
      },
      { skip: !orderData?.orderGroupId },
    );

  const handleSiteGroupLoadingChange = (isLoading: boolean) => {
    setSiteGroupPanelLoading(isLoading);
  };

  const lastModifiedDatetime =
    orderData?.editingDatetime ?? orderData?.submittedDatetime;

  return (
    <PortaledRightDrawer
      setOpen={setOpen}
      open={drawerOpen}
      permanent={screenIsLarge && !forceCollapsed}
      drawerWidth={600}
      header={
        <Tabs
          onChange={(_event, value) => setSelectedPanel(value)}
          value={selectedPanel}
          sx={{
            zIndex: 1000,
            backgroundColor: "background.paper",
            height: 1,
          }}
          slotProps={{
            list: {
              sx: {
                height: 1,
              },
            },
          }}
        >
          <Tab
            data-testid="drawer-details-tab"
            label="Careplan"
            value="details"
          />
          <Tab
            data-testid="drawer-comments-tab"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Comments</span>
                {!!unreadCommentCount && (
                  <Badge
                    badgeContent={unreadCommentCount}
                    color="error"
                    slotProps={{
                      badge: {
                        sx: {
                          transform: "none",
                          position: "relative",
                        },
                      },
                    }}
                  />
                )}
              </Stack>
            }
            value="comments"
          />
        </Tabs>
      }
    >
      <Stack sx={{ padding: 2, overflowY: "auto" }}>
        {selectedPanel === "details" && (
          <Stack gap={2} data-testid="drawer-details">
            <Box>
              {siteGroupPanelLoading ? (
                <Stack>
                  <Skeleton variant="rounded" height={56} />
                </Stack>
              ) : (
                <Stack direction="row" justifyContent="space-between">
                  <Stack>
                    <OrderDetailsRow
                      label="Treating clinic:"
                      value={orderData?.treatingDepartment ?? "-"}
                    />
                    <OrderDetailsRow
                      label="Requested physician:"
                      value={orderData?.treatingRo ?? "-"}
                    />
                    <OrderDetailsRow
                      label="Plan delivery:"
                      value={getDisplayOption(
                        orderData?.planDelivery ?? "-",
                        PLAN_DELIVERY_OPTIONS,
                      )}
                    />
                    <OrderDetailsRow
                      label="Submitted:"
                      value={
                        orderData?.submittedDatetime
                          ? toLocaleDateTimeString(orderData?.submittedDatetime)
                          : "-"
                      }
                    />
                    <OrderDetailsRow
                      label="Last modified:"
                      value={
                        lastModifiedDatetime
                          ? toLocaleDateTimeString(lastModifiedDatetime)
                          : "-"
                      }
                    />
                  </Stack>
                  {isDosimetrist && (
                    <Stack>
                      <Tooltip
                        placement="bottom"
                        title={
                          orderData?.createdFromLumonusAi
                            ? "You cannot edit a careplan submitted by a physician."
                            : orderData?.latestPlanGenerationAttempt
                              ? "You cannot edit a careplan if planning automation has started."
                              : ""
                        }
                      >
                        <span>
                          <Button
                            sx={{ height: 36, width: 88 }}
                            variant="outlined"
                            color="primary"
                            startIcon={<EditIcon />}
                            disabled={disableEditing || isEditing}
                            onClick={() => {
                              setIsEditing(true);
                              handleEditOrder();
                            }}
                          >
                            {isEditing ? (
                              <CircularProgress size={24} />
                            ) : (
                              "Edit"
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>
            {!orderData ? (
              <SiteGroupPanelSkeleton />
            ) : (
              siteGroupData?.map((siteGroup, index) => (
                <SiteGroupDrawer
                  index={index}
                  orderId={orderId}
                  key={siteGroup.id}
                  siteGroup={siteGroup}
                  onLoadingChange={handleSiteGroupLoadingChange}
                  doseUnit={orderData?.doseUnit ?? ""}
                  fractionUnit={orderData?.fractionUnit ?? ""}
                />
              ))
            )}
          </Stack>
        )}
        {selectedPanel === "comments" && orderData && (
          <Comments orderGroupId={orderData.orderGroupId} orderId={orderId} />
        )}
      </Stack>
    </PortaledRightDrawer>
  );
};

export default OrderTrackerDrawer;
